import { useMintLessonEditorPlusV1Lib, useMintLessonVisorPlusV1Lib } from '@oneclick/react-mint-editor';
import * as Sentry from '@sentry/react';
import { cssChunkKeyExistedAtBoot, isCssChunkError } from 'app/utils/chunkReload';
import { DEFAULT_LANG } from 'app/config/config';
import { getLemonadeTranslates } from 'app/utils/lemonade-translates';
import { isLocalhost } from 'core';
import { syncAnalyticsIdentity } from 'core/analytics/analyticsIdentity';
import * as coreEntities from 'core/store/index';
import { getRoleUserString } from 'core/utils/user';
import { CLIENT } from 'core/variables/constant';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/pt';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import packageJson from '../../../package.json';

// Segunda barrera tras setUser (DEU-286): builds antiguas y llamadas que aún pasen datos de
// usuario a Sentry. Del usuario solo sale el guid; nombre, usuario y email son datos personales.
function keepOnlyUserId(event) {
  if (!event.user) return;
  const { id } = event.user;
  if (id === undefined) {
    delete event.user;
  } else {
    event.user = { id };
  }
}

const DOCUMENT_ENGINE_URL = 'https://doc-eng.';
// Con 1 s se escapaban eventos: en los de la v1.40.6 y la v1.41.1 (2CF y 3TY, 2026-10-25/26) el rechazo llegó entre
// 1,1 y 3,9 s después del 5xx (cotas: Sentry da el timestamp del evento al segundo), con más peticiones a doc-eng en
// medio. Que Nutrient espere a terminarlas antes de rechazar es una suposición a partir de las migas.
const DOCUMENT_ENGINE_REJECTION_WINDOW_S = 5;

// Nutrient no comprueba el status de Document Engine: tras un 5xx hace `response.json()` sobre el texto del error y el
// rechazo queda sin capturar (TANGERINE-FRONTOFFICE-2CF, 3TY y 1MZ). El fallo es del backend (TAN-12251). El mensaje
// cambia por navegador, así que se reconoce por el 5xx de antes y por el tipo: SyntaxError (2CF y 3TY) o el objeto que
// rechaza el XHR, que el SDK sube como UnhandledRejection (1MZ); son los únicos en Sentry en 90 días (2026-10-29). Sin
// el tipo, se perdería cualquier fallo propio del visor en esos segundos (un TypeError).
const DOCUMENT_ENGINE_REJECTION_TYPES = new Set(['SyntaxError', 'UnhandledRejection']);

function isRejectionAfterDocumentEngineError(event) {
  const exception = event.exception?.values?.[0];
  if (exception?.mechanism?.type !== 'onunhandledrejection') return false;
  if (!DOCUMENT_ENGINE_REJECTION_TYPES.has(exception.type)) return false;
  return (event.breadcrumbs ?? []).some(
    (crumb) =>
      ['fetch', 'xhr'].includes(crumb.category) &&
      crumb.data?.status_code >= 500 &&
      String(crumb.data?.url ?? '').startsWith(DOCUMENT_ENGINE_URL) &&
      event.timestamp - crumb.timestamp < DOCUMENT_ENGINE_REJECTION_WINDOW_S
  );
}

// Una petición o un play() cancelados (al cambiar de página, al pausar) cuya promesa nadie recoge no son un fallo
// de la app: eran ~18 % de los eventos (DEU-329). Solo los no capturados: uno que se avise a mano sí pasa.
// Mientras no esté replicada en la configuración de Sentry, las builds antiguas siguen enviándolos: docs/sentry/filtros.md.
// Se mira el error lanzado (el último de `values`), no la causa: con `cause`, linkedErrorsIntegration pone la causa
// más profunda primero, y un error propio que envuelve un AbortError sí es un fallo. El SDK marca `handled: false`
// en el primer elemento, por eso esa comprobación recorre todos.
function isUncaughtAbortError(event, originalException) {
  const values = event.exception?.values ?? [];
  if (!values.some((value) => value.mechanism?.handled === false)) return false;
  const thrown = values[values.length - 1];
  return (
    originalException?.name === 'AbortError' ||
    thrown.type === 'AbortError' ||
    String(thrown.value ?? '').startsWith('AbortError')
  );
}

// El Session Replay de Sentry, al cargar un iframe de otro origen (los interactivos del visor), lee `Element` de su
// `contentWindow` y el navegador lo bloquea (TANGERINE-FRONTOFFICE-28V, ~330 eventos/30 d). El fallo es del SDK y no
// afecta al usuario. Lo que acota el filtro es el inicio del mensaje: ningún código nuestro lee `Element` de otra
// ventana (decisión de DEU-310). Mecanismo y `handled` no señalan al replay, porque `browserApiErrorsIntegration` marca
// así cualquier listener, timer, rAF o callback de XHR envuelto (563, de msal, llega igual); solo dejan pasar lo que se
// avisa a mano y lo que llega por `onerror`. Como en AbortError, tipo y mensaje se miran en el error lanzado (el último
// de `values`) y el mecanismo en el primero, que es donde lo pone el SDK cuando hay `cause`.
const REPLAY_CROSS_ORIGIN_IFRAME_MESSAGE = "Failed to read a named property 'Element' from 'Window': Blocked a frame with origin";

function isReplayCrossOriginIframeError(event) {
  const values = event.exception?.values ?? [];
  const mechanism = values[0]?.mechanism;
  const thrown = values[values.length - 1];
  return (
    thrown?.type === 'SecurityError' &&
    mechanism?.type === 'instrument' &&
    mechanism.handled === false &&
    String(thrown.value ?? '').startsWith(REPLAY_CROSS_ORIGIN_IFRAME_MESSAGE)
  );
}

const InitsContainer = () => {
  const currentUser = useSelector((state) => coreEntities.auth.selectors.getUser(state));
  const tenant = useSelector((state) => coreEntities.organization.selectors.getTenant(state));
  const language = useSelector((state) => coreEntities.i18n.selectors.getLanguage(state));
  const { api: apiEditor } = useMintLessonEditorPlusV1Lib();
  const { api: apiVisor } = useMintLessonVisorPlusV1Lib();

  useEffect(() => {
    const supportedLocales = ['pt', 'en', 'es'];
    const locale = supportedLocales.includes(language) ? language : 'es';
    moment.locale(locale);
  }, [language]);

  useEffect(() => {
    if (tenant && !isLocalhost()) {
      Sentry.init({
        dsn: 'https://c583e7250991f783148988b55836f209@o4505759437029376.ingest.us.sentry.io/4506257572626432',
        environment: tenant?.name,
        // Desde package.json y no desde REACT_APP_VERSION: esa variable solo se expande si el
        // build pasa por `npm run build`, y el pipeline de Santillana on-prem no lo garantiza.
        release: `tangerine-frontoffice@${packageJson.version}`,
        // Distingue redespliegues de una misma versión.
        dist: packageJson.date,
        integrations: [
          Sentry.replayIntegration(),
          // Sentry.replayCanvasIntegration()
          Sentry.thirdPartyErrorFilterIntegration({
            // Specify the application keys that you specified in the Sentry bundler plugin
            filterKeys: ['tangerine-frontoffice'],

            // Defines how to handle errors that contain third party stack frames.
            // Possible values are:
            // - 'drop-error-if-contains-third-party-frames'
            // - 'drop-error-if-exclusively-contains-third-party-frames'
            // - 'apply-tag-if-contains-third-party-frames'
            // - 'apply-tag-if-exclusively-contains-third-party-frames'
            // 'apply-tag-if-contains-third-party-frames' instead of the drop variant so that
            // beforeSend still runs for CSS chunk errors (which always contain webpack runtime
            // and React internal frames and would otherwise be silently discarded before
            // beforeSend is ever invoked). Third-party events are then dropped in beforeSend
            // after the chunk-error gate has had a chance to run.
            behaviour: 'apply-tag-if-contains-third-party-frames',
          }),
        ],

        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event, hint) {
          const originalException = hint?.originalException;

          keepOnlyUserId(event);

          // ChunkLoadErrorBoundary sets this tag (for both CSS and JS chunk errors) only after
          // the reload cycle has already run and the failure is confirmed persistent.
          // Always let these through — the boundary already did the right thing.
          if (event.tags?.error_type === 'chunk_load_error') return event;

          // Suppress on first CSS chunk failure; report only when the key existed at boot
          // (= page was already reloaded once for this pathname → persistent failure).
          // cssChunkKeyExistedAtBoot is a one-time snapshot from module evaluation, so it
          // correctly reflects the state before the error listener writes the key.
          if (isCssChunkError(originalException)) {
            // For automatic Sentry captures (window.error / unhandledrejection propagation),
            // only report after the page has already been reloaded once for this route.
            return cssChunkKeyExistedAtBoot ? event : null;
          }

          // thirdPartyErrorFilterIntegration is configured to tag (not drop) events that
          // contain third-party frames, so beforeSend receives them and can apply the
          // chunk-error gate above first. Drop all other third-party-tagged events here.
          if (event.tags?.third_party_code) return null;

          if (isRejectionAfterDocumentEngineError(event)) return null;

          if (isUncaughtAbortError(event, originalException)) return null;

          if (isReplayCrossOriginIframeError(event)) return null;

          // Nutrient, en modo linearizado, rechaza con este string sin que nadie lo capture (TANGERINE-FRONTOFFICE-1M4)
          if (originalException === 'Error making page available') return null;

          const errorMessage = originalException?.message;

          // Comprueba si el mensaje de error es el que quieres ignorar
          const IGNORED_ERROR_PATTERNS = [
            'cannot be parsed as a URL',
            'Failed to fetch',
            '$(iframe).attr',
            'Network Error',   // Axios XHR network failure (capital N and E)
            'network error',   // Firefox lowercase variant
            'NetworkError',    // DOMException variant
            // El navegador integrado de la app de Google en iOS cambia `window.print` por un puente propio que no existe
            // en la página: ahí no se puede imprimir (TANGERINE-FRONTOFFICE-55X, 5A2 y 55Y)
            'webkit.messageHandlers.print',
          ];
          if (IGNORED_ERROR_PATTERNS.some((p) => errorMessage?.includes(p))) {
            // No enviar el evento a Sentry
            return null;
          }

          return event;
        },
      });
    }
  }, [tenant]);

  useEffect(() => {
    if (currentUser && !isLocalhost()) {
      Sentry.setUser({ id: currentUser.guid });
      Sentry.setTag('user_role', currentUser.role_name);
    } else if (!currentUser && !isLocalhost()) {
      Sentry.setUser(null);
      Sentry.setTag('user_role', undefined);
    }
  }, [currentUser]);

  useEffect(() => {
    syncAnalyticsIdentity(currentUser, language);
  }, [currentUser, language]);

  useEffect(() => {
    if (currentUser && tenant) {
      initStonly();
    }
  }, [currentUser, tenant]);

  useEffect(() => {
    getLemonadeTranslates(apiEditor, apiVisor);
  }, []);

  function initStonly() {
    if (window.StonlyWidget !== undefined) {
      window.StonlyWidget('enableDebug');
      if (currentUser?.guid) {
        let data = {
          lang: currentUser.lang_id || DEFAULT_LANG,
          role: getRoleUserString(currentUser),
          'user-segment': currentUser.is_demo === 1 ? 'demo' : '',
        };
        if (tenant?.guid === CLIENT.GOTITBYME) {
          // Para GOTITBYME, cambiamos los nombres de los campos
          data = {
            lang: currentUser.lang_id || DEFAULT_LANG,
            'role-gotit': getRoleUserString(currentUser),
            'user-segment-gotit': currentUser.is_demo === 1 ? 'demo' : '',
          };
        }
        window.StonlyWidget('identify', currentUser.guid, data);
      } else {
        window.StonlyWidget('identify', null, { 'user-segment': '' });
      }
    }
  }

  return <></>;
};

export default InitsContainer;
